import { useSyncExternalStore } from 'react';

// A "something was written" signal, scoped by TAG so a write only revalidates the
// reads that depend on the changed data — not every mounted hook. The tag is the
// data domain, derived from the API path: a write to /api/account/* signals
// 'account', /api/bookings/* signals 'bookings', and so on. A read declares which
// tags invalidate it via useResource's `invalidatedBy`, and revalidates only when
// one of those tags is bumped.
//
// DRY + global + back-compatible:
//   • Every write also bumps the WILDCARD tag, and a read with NO declared tags
//     listens to the wildcard — so existing call sites (and other pages) keep the
//     old "any write refreshes me" behaviour until they opt into tags.
//   • `invalidatedBy: []` opts OUT of write-revalidation entirely (for reads whose
//     data isn't write-signalled, e.g. page-view counts ingested fire-and-forget).
//
// The revalidation itself is the hooks' existing silent, content-holding,
// cache-bypassing fetch (fetchPlan) — no loading flash, no per-call-site wiring.

export const WILDCARD = '*';

const versions = new Map<string, number>();
const listeners = new Set<() => void>();

const versionOf = (tag: string): number => versions.get(tag) ?? 0;

// Bump the given tags (plus the wildcard) and notify subscribers. No tags → just
// the wildcard (a generic write whose domain we don't know).
export function signalMutation(tags: string[] = []): void {
  for (const tag of [WILDCARD, ...tags]) versions.set(tag, versionOf(tag) + 1);
  for (const l of listeners) l();
}

export function subscribeMutation(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

// The combined version across a set of tags — the snapshot a hook revalidates on.
// Default is the wildcard (revalidate on every write); `[]` means never.
export function getMutationVersion(tags: string[] = [WILDCARD]): number {
  let sum = 0;
  for (const tag of tags) sum += versionOf(tag);
  return sum;
}

// Reactive tick that changes whenever one of `tags` is bumped. A data hook folds
// this into its revalidate trigger. Returns a stable primitive, so no render loop.
export function useMutationTick(tags: string[] = [WILDCARD]): number {
  const snapshot = () => getMutationVersion(tags);
  return useSyncExternalStore(subscribeMutation, snapshot, snapshot);
}
